import Modal from '@/components/Modal';
import NumericKeyboard, { NumericKeyboardProps } from '@/components/NumericKeyboard';
import Price from '@/components/Price';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { convertRate, Coupon, CouponType, getCouponTypeDesc } from '@/models/coupon';
import { InventoryList, InventoryListStatus, InventoryTypeEnum } from '@/models/inventory';
import { Order } from '@/models/order';
import { Staff } from '@/models/staff';
import { SupplierLog, SupplierLogTypeDescMap, SupplierLogTypeEnum } from '@/models/Supplier';
import { User } from '@/models/user';
import { assertUnreachable } from '@/utils/common';
import limitX from '@/utils/imgUtils';
import { playSound } from '@/utils/sound';
import { toHttpsUrl } from '@/utils/url';
import { QuestionCircleOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Input, Space, Tag } from 'antd';
import { AvatarProps } from 'antd/es/avatar';
import { ButtonProps } from 'antd/es/button/button';
import { SpaceProps } from 'antd/es/space';
import BigNumber from 'bignumber.js';
import React, { ReactElement, useCallback, useContext } from 'react';

export const No_Margin = { margin: 0 };
export const Brand_Logo = require('../assets/logo.svg');

export const formatWithLineBreaks = (text?: string): React.ReactNode[] => {
  if (text === null || text === undefined) return null;

  const lines = text.split('\n');

  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {line === '' ? <>&nbsp;</> : line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

export const useSkuImg = () => {
  const [{ assetsPrefix }] = useContext(BaseInfoContext);
  return useCallback((img: string, limit?: number) => {
    if (img) {
      if (img.startsWith('http')) {
        return img;
      } else {
        return `${assetsPrefix}${img}${limit ? limitX(limit) : ''}`;
      }
    } else {
      return require('../assets/default-product.png');
    }
  }, []);
};

export const getAdminAvatar = (
  adminOrAvatar: Staff | string,
  assetsPrefix: string,
  avatarProps: AvatarProps = {}
): React.ReactNode => {
  if (adminOrAvatar) {
    const avatar = typeof adminOrAvatar === 'string' ? adminOrAvatar : adminOrAvatar.avatar;

    if (avatar) {
      return <Avatar draggable={false} src={`${assetsPrefix}${avatar}`} {...avatarProps} />;
    }
  }
  return <Avatar draggable={false} icon={<UserOutlined />} {...avatarProps} />;
};

export const getUserAvatar = (userOrAvatar?: User | string, avatarProps: AvatarProps = {}): React.ReactNode => {
  const avatar = typeof userOrAvatar === 'string' ? userOrAvatar : userOrAvatar?.avatar;

  if (avatar) {
    return <Avatar draggable={false} src={`${toHttpsUrl(avatar)}`} {...avatarProps} />;
  }
  return <Avatar draggable={false} icon={<UserOutlined />} {...avatarProps} />;
};

export function modalPrompt({
  title = '请输入',
  desc,
  icon = <QuestionCircleOutlined />,
  onOk = () => {},
  onCancel = () => {},
  useNumberKeyboard = false,
  numberKeyboardProps = {},
}: {
  title?: string;
  desc?: React.ReactElement;
  icon?: ReactElement;
  onOk: (value: any) => Promise<void> | void | boolean;
  onCancel?: () => void;
  useNumberKeyboard?: boolean;
  numberKeyboardProps?: NumericKeyboardProps;
}) {
  let value = '';
  const okBtnCommonProps: ButtonProps = {
    size: 'large',
    style: useNumberKeyboard ? { display: 'none' } : {},
  };

  let requestClose = () => {};

  let modal = Modal.confirm({
    maskClosable: false,
    autoFocusButton: null,
    title,
    icon,
    content: (
      <div>
        {desc}
        {useNumberKeyboard ? (
          <NumericKeyboard
            onChange={v => {
              value = `${v}`.trim();
            }}
            onComplete={() => {
              requestClose();
            }}
            {...numberKeyboardProps}
          />
        ) : (
          <Input
            autoFocus
            onChange={({ target: { value: v } }) => {
              value = `${v}`.trim();
            }}
            onPressEnter={() => {
              requestClose();
            }}
          />
        )}
      </div>
    ),
    okType: undefined,
    okText: '确定',
    cancelText: '取消',
    onOk: () => {
      playSound('click');
      return onOk(value);
    },
    onCancel: () => {
      playSound('click');
      onCancel();
    },
    okButtonProps: {
      ...okBtnCommonProps,
    },
    cancelButtonProps: {
      size: 'large',
    },
  });

  let loading = false;

  requestClose = () => {
    if (loading) {
      return;
    }
    loading = true;
    const v = onOk(value);
    if (v instanceof Promise) {
      modal.update({
        okButtonProps: { loading: loading, ...okBtnCommonProps },
        cancelButtonProps: { loading: loading, size: 'large' },
      });
      v.then(() => {
        modal.destroy();
      }).catch(() => {
        loading = false;
        modal.update({
          okButtonProps: { loading: loading, ...okBtnCommonProps },
          cancelButtonProps: { loading: loading, size: 'large' },
        });
      });
    } else if (typeof v === 'boolean') {
      if (v !== false) {
        modal.destroy();
      } else {
        loading = false;
      }
    } else {
      modal.destroy();
    }
  };
}

export const getSupplierLogTypeDesc = (log: SupplierLog) => {
  switch (log.type) {
    case SupplierLogTypeEnum.Purchase_Prepay:
      return (
        <Tag color={'green'} style={No_Margin}>
          {SupplierLogTypeDescMap.get(SupplierLogTypeEnum.Purchase_Prepay)}
        </Tag>
      );
    case SupplierLogTypeEnum.Reconcile:
      return (
        <Tag color={'orange'} style={No_Margin}>
          {SupplierLogTypeDescMap.get(SupplierLogTypeEnum.Reconcile)}
        </Tag>
      );
    case SupplierLogTypeEnum.Settle:
      return (
        <Tag color={'red'} style={No_Margin}>
          {SupplierLogTypeDescMap.get(SupplierLogTypeEnum.Settle)}
        </Tag>
      );
  }
  return assertUnreachable(log.type);
};

export const getInventoryListTypeDesc = (inventoryList: InventoryList) => {
  switch (inventoryList.type) {
    case InventoryTypeEnum.Purchase:
      return <Tag style={No_Margin}>进货单</Tag>;
    case InventoryTypeEnum.StockTaking:
      return <Tag style={No_Margin}>盘点单</Tag>;
  }
  return assertUnreachable(inventoryList.type);
};

export const getInventoryListStatusDesc = (inventoryList: InventoryList) => {
  switch (inventoryList.status) {
    case InventoryListStatus.Created:
      return (
        <Tag color={'blue'} style={No_Margin}>
          待对账
        </Tag>
      );
    case InventoryListStatus.Reconciled:
      return (
        <Tag color={'geekblue'} style={No_Margin}>
          待结算
        </Tag>
      );
    case InventoryListStatus.Settled:
      return (
        <Tag color={'green'} style={No_Margin}>
          已结清
        </Tag>
      );
    case InventoryListStatus.Cleared:
      return (
        <Tag color={'green'} style={No_Margin}>
          已完成
        </Tag>
      );
  }
  return assertUnreachable(inventoryList.status);
};

export const getInventoryListStatusActionDesc = (inventoryList: InventoryList) => {
  switch (inventoryList.status) {
    case InventoryListStatus.Created:
      return <>对账</>;
    case InventoryListStatus.Reconciled:
      return <>结算</>;
    case InventoryListStatus.Settled:
      return <>已结清</>;
    case InventoryListStatus.Cleared:
      return <>已完成</>;
  }
  return assertUnreachable(inventoryList.status);
};

export const splitToNewLine = (str: string, separator = ' ') => {
  const arr = str.split(separator);
  return (
    <>
      {arr.map((item, index) => (
        <React.Fragment key={index}>
          {index !== 0 && <br />}
          {item}
        </React.Fragment>
      ))}
    </>
  );
};

export const getCouponTypeTag = (type: CouponType) => {
  return <Tag color={type === 1 ? 'magenta' : 'orange'}>{getCouponTypeDesc(type)}</Tag>;
};

export const getCouponRuleDesc = (coupon: Coupon) => {
  return (
    <>
      {coupon.couponType === 1 ? (
        <>
          满 {coupon.min} 打 {convertRate(coupon.discount)} 折
        </>
      ) : (
        <>
          满 {coupon.min} 减 {coupon.discount}
        </>
      )}
    </>
  );
};

export const Custom_Display_Image = {
  CARD: '',
  PRICE: 'https://cdn.il-canto.com/assets/images/bg/price2.png',
};

export enum Custom_Display_Type_ENUM {
  CARD = 'CARD',
  PRICE = 'PRICE',
}

const orderTotalAmountDisplayDefaultOptions = {
  direction: 'horizontal' as const,
  displayMode: 'default' as const,
};
export const orderTotalAmountDisplay = (
  order: Order,
  optionsParam: {
    direction?: SpaceProps['direction'];
    displayMode?: 'default' | 'small';
  } = orderTotalAmountDisplayDefaultOptions
) => {
  const options = Object.assign({}, orderTotalAmountDisplayDefaultOptions, optionsParam);
  const isDefaultDisplayMode = options.displayMode === 'default';
  const actualPrice = order.actualPrice ? new BigNumber(order.actualPrice) : new BigNumber(0);
  const balancePrice = order.balancePrice ? new BigNumber(order.balancePrice) : new BigNumber(0);
  const totalPrice = balancePrice.plus(actualPrice);

  const balance = order.balancePrice ? (
    <>
      {isDefaultDisplayMode && <span>+</span>}
      <span>
        余额 <Price value={balancePrice} />
      </span>
    </>
  ) : null;
  const total = (
    <span>
      {' '}
      = <Price value={totalPrice} />
    </span>
  );
  return (
    <Space size={isDefaultDisplayMode ? 5 : 0} direction={options.direction}>
      {isDefaultDisplayMode ? <span>合计:</span> : null}
      <span>
        {balance ? <>实付</> : null} <Price value={actualPrice} />
      </span>
      {balance}
      {balance ? total : null}
    </Space>
  );
};

export const insertAtCursor = (
  text: string,
  textarea: HTMLTextAreaElement,
  value: string,
  onChange: (value: string) => void
) => {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  // const value = textarea.value;

  // 构造新内容
  const newValue = value.substring(0, start) + text + value.substring(end);

  // 设置新值
  onChange(newValue);

  // 更新光标位置
  requestAnimationFrame(() => {
    const cursorPosition = start + text.length;
    textarea.setSelectionRange(cursorPosition, cursorPosition);
    textarea.focus();
  });
};

export const FormLayout = {
  ['6-18']: {
    labelCol: { span: 6 },
    wrapperCol: { span: 16 },
    labelAlign: 'right' as 'right',
    tailWrapperCol: {
      sm: { offset: 6, span: 18 },
      xs: { span: 24, offset: 0 },
    },
  },
  ['4-20']: {
    labelCol: { span: 4 },
    wrapperCol: { span: 20 },
    labelAlign: 'right' as 'right',
    tailWrapperCol: {
      sm: { offset: 4, span: 10 },
      xs: { span: 24, offset: 0 },
    },
  },
};
export const FormLayout_6_18 = FormLayout['6-18'];
export const FormLayout_4_20 = FormLayout['4-20'];

/**
 * 显示分钟数
 * 0. 显示为 h小时:m分 的效果
 * 1. 小时部分超过1小时才显示
 * 2. 分钟部分若为0，则不显示
 * @param minutes
 */
export const minutesToDisplay = (minutes: number, fallback = '') => {
  if (minutes <= 0) return fallback;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  let parts: string[] = [];
  if (hours > 0) {
    parts.push(`${hours}小时`);
  }
  if (mins > 0) {
    parts.push(`${mins}分`);
  }
  return parts.join('');
};

export const Style_Display_None = { style: { display: 'none' } };
export const Table_Align_Center = 'center' as 'center';
export const Table_Align_Right = 'right' as 'right';

export const formatAny = (value: unknown, fallback = '-') => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  return String(value);
};

export const formatBoolean = (value?: boolean) => {
  if (value === undefined || value === null) {
    return '-';
  }
  return value ? <Tag color={'#52c41a'}>是</Tag> : <Tag>否</Tag>;
};
